import { usePermissions } from '@/hooks/usePermissions';
import useTemplateSaveErrorToast from '@/hooks/useTemplateSaveErrorToast';
import { FC, useMemo, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { getAcceptedFileTypes } from '@payaca/helpers/fileHelper';
import { roundToDecimalPlaces } from '@payaca/helpers/numberHelper';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { UploadsPermissions } from '@payaca/permissions/uploads/uploads.permissions';
import { updateTemplate } from '@payaca/store/templates/templatesActions';
import {
  PublicEntityTemplate,
  PublicHydratedEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import { getTimeDurationFromDurationString } from '@payaca/utilities/timeUtilities';
import { WithOptional } from '@payaca/utilities/types';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import TagSelectionControl from '../tagSelectionControl/TagSelectionControl';
import UploadCollectionControl from '../uploadCollectionControl/UploadCollectionControl';
import './EntityTemplateReadView.sass';
import './EntityTemplateScheduledEventReadView.sass';
import ScheduledEventLinkedTaskTemplatesPanel from './ScheduledEventLinkedTaskTemplatesPanel';

const acceptFileTypes = getAcceptedFileTypes(['document', 'image', 'video']);

type Props = {
  entityTemplate: PublicHydratedEntityTemplate<'scheduledEvent'>;
  onUpdateEntityTemplateSuccess?: (
    updatedEntityTemplate: PublicHydratedEntityTemplate<TemplatableEntity>
  ) => void;
};
const EntityTemplateScheduledEventReadView: FC<Props> = ({
  entityTemplate,
  onUpdateEntityTemplateSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const onTemplateSaveError = useTemplateSaveErrorToast();

  const { userHasRequiredPermission } = usePermissions();
  const userCanDeleteAttachment = useMemo(() => {
    return userHasRequiredPermission([UploadsPermissions.DELETE_UPLOAD]);
  }, [userHasRequiredPermission]);

  // Parse the stored ISO duration directly rather than by date arithmetic
  // against "now" — moment's day arithmetic is calendar-aware, so across a DST
  // clock change 3 days diffs as 73 hours and displays as "3.04 days".
  const duration = useMemo<any>(() => {
    if (!entityTemplate?.data?.duration) return null;
    return getTimeDurationFromDurationString(entityTemplate.data.duration);
  }, [entityTemplate?.data.duration]);

  if (!entityTemplate) return null;

  return (
    <div className={'entity-template-scheduled-event-read-view'}>
      <PermissionGuard
        renderIfHasPermissions={[TemplatesPermissions.PERSIST_TEMPLATE]}
      >
        <TagSelectionControl
          selectedTagIds={
            (
              entityTemplate as WithOptional<
                PublicEntityTemplate<'scheduledEvent'>,
                'publicId'
              >
            ).data?.tagIds || []
          }
          onSelectedTagsChange={(tagIds) => {
            dispatch(
              updateTemplate.request({
                publicId: entityTemplate.publicId,
                template: {
                  entityType: entityTemplate.entityType,
                  data: {
                    ...entityTemplate.data,
                    tagIds: tagIds,
                  },
                },
                callback: onUpdateEntityTemplateSuccess,
                onErrorCallback: onTemplateSaveError,
              })
            );
          }}
        />
      </PermissionGuard>
      <div>
        {entityTemplate?.data.name && (
          <h1 className="name">{entityTemplate.data.name}</h1>
        )}
        {duration && (
          <span className="duration">
            <span className="label">Duration:</span>{' '}
            {roundToDecimalPlaces(duration.value)} {duration.label}
          </span>
        )}
      </div>
      {entityTemplate?.data.description && (
        <div className="description">
          <div className="label">Description</div>
          <div>{entityTemplate.data.description}</div>
        </div>
      )}
      <div className="tasks">
        <div className="label">Add linked tasks</div>
        <ScheduledEventLinkedTaskTemplatesPanel
          taskTemplates={entityTemplate.data?.tasks || []}
          onChange={(taskTemplates) => {
            return new Promise((resolve, reject) => {
              dispatch(
                updateTemplate.request({
                  publicId: entityTemplate.publicId,
                  template: {
                    entityType: entityTemplate.entityType,
                    data: {
                      ...entityTemplate.data,
                      tasks: taskTemplates,
                    },
                  },
                  callback: (et) => {
                    onUpdateEntityTemplateSuccess?.(et);
                    resolve();
                  },
                  onErrorCallback: (error) => {
                    onTemplateSaveError();
                    reject(error);
                  },
                })
              );
            });
          }}
        />
      </div>
      <PermissionGuard
        renderIfHasPermissions={[UploadsPermissions.GET_UPLOADS]}
      >
        <UploadCollectionControl
          label="Attachments"
          uploadIds={entityTemplate.data?.uploadIds || []}
          onUploadCollectionChange={(uploadIds) => {
            dispatch(
              updateTemplate.request({
                publicId: entityTemplate.publicId,
                template: {
                  entityType: entityTemplate.entityType,
                  data: {
                    ...entityTemplate.data,
                    uploadIds: uploadIds,
                  },
                },
                callback: onUpdateEntityTemplateSuccess,
                onErrorCallback: onTemplateSaveError,
              })
            );
          }}
          allowMultipleUploads={true}
          canRemove={userCanDeleteAttachment}
          acceptFileTypes={acceptFileTypes}
        />
      </PermissionGuard>
    </div>
  );
};
export default EntityTemplateScheduledEventReadView;
